/**
 * Epic 278 Phase 3 Slice B — Scheduled Scan status for the Scans register.
 * Consumes Epic 277 run statuses only. Do not invent missed/success states.
 */

import { computeNextSchedulePreview } from '../scheduledScans/scheduleRecurrencePreview';
import type {
  ScanDefinition,
  ScheduledScanRun,
  ScheduledScanSchedule,
} from '../../services/scheduledScansApi';
import { scheduledDefinitionWorkflowId } from './currentScanTruth';
import { buyerScanDefinitionDisplayName } from '../buyerFacing/conditionDisplayNames';

export const SCHEDULED_RUN_STATUSES = [
  'EXPECTED',
  'QUEUED',
  'RUNNING',
  'COMPLETED',
  'FAILED',
  'DEFERRED',
  'SKIPPED_OVERLAP',
  'SKIPPED_MISSED',
  'CANCELLED',
  'INSUFFICIENT_COVERAGE',
] as const;

export type ScheduledRunStatus = (typeof SCHEDULED_RUN_STATUSES)[number];

export type ScheduledRunCustomerLabel =
  | 'Scheduled'
  | 'Queued'
  | 'Running'
  | 'Completed'
  | 'Failed'
  | 'Deferred'
  | 'Skipped (overlap)'
  | 'Missed'
  | 'Cancelled'
  | 'Insufficient coverage';

export type ScheduledRunStatusTone = 'default' | 'success' | 'warning' | 'error';

export type ScheduledRunCustomerStatus = {
  status: ScheduledRunStatus;
  label: ScheduledRunCustomerLabel;
  tone: ScheduledRunStatusTone;
  /** Operator-facing notice when Epic 277 status carries deferral / miss / overlap meaning. */
  notice: string | null;
};

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'] as const;

const ACTIVE_STATUSES = new Set<ScheduledRunStatus>([
  'EXPECTED',
  'QUEUED',
  'RUNNING',
  'DEFERRED',
]);

const STATUS_MAP: Record<ScheduledRunStatus, Omit<ScheduledRunCustomerStatus, 'status'>> = {
  EXPECTED: {
    label: 'Scheduled',
    tone: 'default',
    notice: null,
  },
  QUEUED: {
    label: 'Queued',
    tone: 'warning',
    notice: null,
  },
  RUNNING: {
    label: 'Running',
    tone: 'warning',
    notice: null,
  },
  COMPLETED: {
    label: 'Completed',
    tone: 'success',
    notice: null,
  },
  FAILED: {
    label: 'Failed',
    tone: 'error',
    notice: null,
  },
  DEFERRED: {
    label: 'Deferred',
    tone: 'warning',
    notice: 'Deferred — another scheduled run occupies the active slot',
  },
  SKIPPED_OVERLAP: {
    label: 'Skipped (overlap)',
    tone: 'warning',
    notice: 'Skipped due to overlap with an active scheduled run',
  },
  SKIPPED_MISSED: {
    label: 'Missed',
    tone: 'warning',
    notice: 'Missed occurrence (materializer recovery)',
  },
  CANCELLED: {
    label: 'Cancelled',
    tone: 'error',
    notice: null,
  },
  INSUFFICIENT_COVERAGE: {
    label: 'Insufficient coverage',
    tone: 'warning',
    notice: null,
  },
};

function normalizeStatus(value: unknown): string {
  return String(value || '').trim().toUpperCase();
}

/** Map Epic 277 run status → controlled customer label. Unknown → null (never invent Completed). */
export function mapScheduledRunCustomerStatus(
  status: string | null | undefined,
): ScheduledRunCustomerStatus | null {
  const key = normalizeStatus(status);
  if (!(key in STATUS_MAP)) return null;
  const mapped = STATUS_MAP[key as ScheduledRunStatus];
  return { status: key as ScheduledRunStatus, ...mapped };
}

/** Internal proof-readiness tokens. Never a buyer explanation for Scan outcome. */
export const INTERNAL_PROOF_READINESS_TOKENS = new Set([
  'READY',
  'READY_WITH_WARNINGS',
  'NOT_READY',
  'NEEDS_REVIEW',
]);

export function customerSafeScheduledRunNotice(value: unknown): string | null {
  const detail = String(value || '').trim();
  if (!detail) return null;
  if (INTERNAL_PROOF_READINESS_TOKENS.has(detail.toUpperCase())) return null;
  const normalized = detail.toLowerCase();
  if (
    normalized.includes('missing required scheduled scan report sections')
    || normalized.includes('incident_groups')
  ) {
    return 'Scheduled Scan package could not be prepared. Review the Scan setup or retry after required evidence is available.';
  }
  return detail.length <= 180 && !/^error$/i.test(detail) ? detail : null;
}

/** Stable cadence TZ label: UTC stays UTC; other IANA ids are shown as stored. */
export function formatScheduleTimezoneLabel(timeZone?: string | null): string {
  const tz = String(timeZone || '').trim();
  if (!tz) return '';
  if (/^(UTC|Etc\/UTC|GMT|Etc\/GMT)$/i.test(tz)) return 'UTC';
  return tz;
}

export function formatScheduleCadenceLabel(schedule: Pick<
  ScheduledScanSchedule,
  'cadence' | 'day_of_week' | 'execution_time_local' | 'timezone'
>): string {
  const time = String(schedule.execution_time_local || '').slice(0, 5) || '—';
  const tz = formatScheduleTimezoneLabel(schedule.timezone);
  const suffix = tz ? ` ${tz}` : '';
  if (schedule.cadence === 'WEEKLY') {
    const day = WEEKDAYS[schedule.day_of_week ?? -1] || 'day';
    return `Weekly on ${day} at ${time}${suffix}`;
  }
  if (schedule.cadence === 'DAILY') {
    return `Daily at ${time}${suffix}`;
  }
  return 'Off';
}

function formatInstantInZone(date: Date, timeZone: string): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone,
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(date);
}

function naiveCivilToDate(raw: string, timeZone: string): Date | null {
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  const second = Number(match[6] || 0);
  const target = Date.UTC(year, month - 1, day, hour, minute, second);
  let guess = target;
  for (let attempt = 0; attempt < 4; attempt += 1) {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(new Date(guess));
    const value = (type: string) => Number(parts.find((part) => part.type === type)?.value);
    const shown = Date.UTC(
      value('year'),
      value('month') - 1,
      value('day'),
      value('hour'),
      value('minute'),
      value('second'),
    );
    guess += target - shown;
  }
  return new Date(guess);
}

/** Trailing Z/offset on occurrence_at_local is API serialization of a naive civil clock, not UTC. */
const OCCURRENCE_LOCAL_ZONE_SUFFIX = /(?:Z|[+-]\d{2}:?\d{2})$/i;

export function formatOccurrenceLocal(value?: string | null, timeZone?: string | null): string {
  const raw = String(value || '').trim();
  if (!raw) return '—';
  const tz = String(timeZone || '').trim();
  // occurrence_at_local is civil clock in schedule.timezone. Do not reuse this path
  // for genuine UTC fields (occurrence_at_utc, created_at, signed timestamps, etc.).
  if (tz) {
    const civil = raw.replace(OCCURRENCE_LOCAL_ZONE_SUFFIX, '');
    const asZone = naiveCivilToDate(civil, tz);
    if (asZone && !Number.isNaN(asZone.getTime())) return formatInstantInZone(asZone, tz);
  }
  if (OCCURRENCE_LOCAL_ZONE_SUFFIX.test(raw)) {
    const parsed = new Date(raw);
    if (!Number.isNaN(parsed.getTime())) return formatInstantInZone(parsed, 'UTC');
  }
  return raw.replace('T', ' ').slice(0, 16);
}

export function formatOccurrenceUtc(value?: string | null, timeZone?: string | null): string {
  const raw = String(value || '').trim();
  if (!raw) return '—';
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return '—';
  return formatInstantInZone(parsed, String(timeZone || '').trim() || 'UTC');
}

export function formatObservationWindowLabel(run: Pick<
  ScheduledScanRun,
  'window_start_utc' | 'window_end_utc'
>): string | null {
  const start = String(run.window_start_utc || '').trim();
  const end = String(run.window_end_utc || '').trim();
  if (!start || !end) return null;
  const startDate = new Date(start);
  const endDate = new Date(end);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return null;
  const fmt = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  return `${fmt.format(startDate)} – ${fmt.format(endDate)}`;
}

function runSortKey(run: ScheduledScanRun): number {
  const utc = Date.parse(String(run.occurrence_at_utc || ''));
  return Number.isNaN(utc) ? 0 : utc;
}

/**
 * Prefer an active/deferred occurrence; otherwise the latest materialized run.
 * Never invents a run when the list is empty.
 */
export function selectPrimaryScheduledRun(runs: ScheduledScanRun[]): ScheduledScanRun | null {
  if (!runs.length) return null;
  const sorted = [...runs].sort((a, b) => runSortKey(b) - runSortKey(a));
  const active = sorted.find((run) => ACTIVE_STATUSES.has(normalizeStatus(run.status) as ScheduledRunStatus));
  return active || sorted[0] || null;
}

function nextOccurrencePreview(schedule: ScheduledScanSchedule): {
  label: string;
  atUtc: string;
} | null {
  if (!schedule.enabled || schedule.cadence === 'OFF') return null;
  try {
    const preview = computeNextSchedulePreview({
      enabled: schedule.enabled,
      cadence: schedule.cadence,
      dayOfWeek: schedule.day_of_week ?? 0,
      executionTime: schedule.execution_time_local,
      timezone: schedule.timezone,
      windowSeconds: schedule.window_length_seconds,
    });
    const label = String(preview?.occurrenceLabel || '').trim();
    const atUtc = String(preview?.occurrenceAtUtc || '').trim();
    if (!label || !atUtc) return null;
    return { label, atUtc };
  } catch {
    return null;
  }
}

export function formatNextOccurrencePreview(schedule: ScheduledScanSchedule): string | null {
  return nextOccurrencePreview(schedule)?.label || null;
}

export type ScheduledScanStatusRow = {
  definitionId: string;
  definitionName: string;
  scheduleId: string;
  cadenceLabel: string;
  occurrenceLabel: string;
  nextOccurrenceLabel: string | null;
  /** Instant used to pick the earliest upcoming occurrence; not shown in the UI. */
  nextOccurrenceAtUtc: string | null;
  /** Controlled customer label, or null when no run / unrecognized Epic 277 status. */
  statusLabel: ScheduledRunCustomerLabel | null;
  statusTone: ScheduledRunStatusTone;
  notice: string | null;
  observationWindowLabel: string | null;
  runId: string | null;
  /** Raw Epic 277 status when present; never used as a success signal by itself. */
  rawStatus: string | null;
  resultSummaryId: string | null;
  reportId: string | null;
  terminalReasonCode: string | null;
  terminalReasonMessage: string | null;
  cameraIds: string[];
  /** Full workflow identity; optional so existing fixtures keep compiling. */
  workflowId?: string | null;
};

export type ScheduledScanStatusBuildInput = {
  definition: ScanDefinition;
  schedule: ScheduledScanSchedule;
  runs: ScheduledScanRun[];
};

/** Build one status row from Epic 277 definition + enabled schedule + runs. */
export function buildScheduledScanStatusRow(
  input: ScheduledScanStatusBuildInput,
): ScheduledScanStatusRow | null {
  const { definition, schedule, runs } = input;
  if (!schedule.enabled || schedule.cadence === 'OFF') return null;

  const matchingRuns = runs.filter((run) => (
    String(run.schedule_id) === String(schedule.id)
    && Number(run.schedule_version) === Number(schedule.version)
    && String(run.scan_definition_id) === String(definition.scan_definition_id)
    && Number(run.scan_definition_version) === Number(definition.version)
  ));
  const primary = selectPrimaryScheduledRun(matchingRuns);
  const mapped = primary ? mapScheduledRunCustomerStatus(primary.status) : null;
  const terminalNotice = customerSafeScheduledRunNotice(primary?.terminal_reason_message);
  const nextPreview = nextOccurrencePreview(schedule);

  let notice = mapped?.notice || null;
  if (!notice && terminalNotice && mapped && (
    mapped.status === 'DEFERRED'
    || mapped.status === 'SKIPPED_OVERLAP'
    || mapped.status === 'SKIPPED_MISSED'
    || mapped.status === 'FAILED'
    || mapped.status === 'CANCELLED'
  )) {
    notice = terminalNotice;
  }

  return {
    definitionId: definition.scan_definition_id,
    definitionName: buyerScanDefinitionDisplayName(definition.name) || 'Scheduled Scan',
    scheduleId: schedule.id,
    cadenceLabel: formatScheduleCadenceLabel(schedule),
    occurrenceLabel: primary
      ? (primary.occurrence_at_local
        ? formatOccurrenceLocal(primary.occurrence_at_local, schedule.timezone)
        : formatOccurrenceUtc(primary.occurrence_at_utc, schedule.timezone))
      : '—',
    nextOccurrenceLabel: nextPreview?.label || null,
    nextOccurrenceAtUtc: nextPreview?.atUtc || null,
    statusLabel: mapped?.label ?? null,
    // Unknown Epic 277 status: neutral tone — never invent Completed/success.
    statusTone: mapped?.tone || 'default',
    notice: mapped
      ? notice
      : (primary ? `Unrecognized run status: ${normalizeStatus(primary.status)}` : null),
    observationWindowLabel: primary ? formatObservationWindowLabel(primary) : null,
    runId: primary?.id || null,
    rawStatus: primary ? normalizeStatus(primary.status) : null,
    resultSummaryId: String(primary?.links?.result_summary_id || '').trim() || null,
    reportId: String((primary?.links as { report_id?: string | null } | undefined)?.report_id || '').trim() || null,
    terminalReasonCode: String(primary?.terminal_reason_code || '').trim() || null,
    terminalReasonMessage: String(primary?.terminal_reason_message || '').trim() || null,
    cameraIds: Array.isArray(definition.scope?.camera_ids)
      ? (definition.scope?.camera_ids as unknown[]).map((id) => String(id || '').trim()).filter(Boolean)
      : [],
    workflowId: scheduledDefinitionWorkflowId(definition) || null,
  };
}

export function buildScheduledScanStatusRows(
  inputs: ScheduledScanStatusBuildInput[],
): ScheduledScanStatusRow[] {
  const rows: ScheduledScanStatusRow[] = [];
  for (const input of inputs) {
    const row = buildScheduledScanStatusRow(input);
    if (row) rows.push(row);
  }
  return rows.sort((a, b) => a.definitionName.localeCompare(b.definitionName));
}

/** Earliest upcoming occurrence by datetime, not definition-name order. */
export function selectEarliestNextOccurrenceLabel(rows: ScheduledScanStatusRow[]): string | null {
  const dated = rows
    .map((row) => ({
      label: String(row.nextOccurrenceLabel || '').trim(),
      at: Date.parse(String(row.nextOccurrenceAtUtc || '')),
    }))
    .filter((row) => row.label && Number.isFinite(row.at));
  if (dated.length) {
    dated.sort((a, b) => a.at - b.at);
    return dated[0].label;
  }
  return rows.map((row) => String(row.nextOccurrenceLabel || '').trim()).find(Boolean) || null;
}

export function scheduledScanDefinitionsHistoryPath(siteId: string): string {
  const params = new URLSearchParams();
  params.set('site_id', siteId);
  return `/acceptance/scan-definitions?${params.toString()}`;
}
